import { useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, fmtInt } from "@/lib/api";
import { Search } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

export default function StyleSalesHistory() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [searched, setSearched] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setBusy(true); setErr(""); setSearched(true);
    try {
      const { data } = await api.get("/reports/style-sales", { params: { q: query.trim() } });
      setData(data);
    } catch (e) {
      setErr("Could not fetch results. Please try again.");
      setData(null);
    } finally { setBusy(false); }
  };

  const onKeyDown = (e) => { if (e.key === "Enter") search(); };

  return (
    <div data-testid="style-sales-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Style Sales History</h1>
          <p className="page-sub">Search a style to see every invoice it was sold in.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="field">
          <label>Search Style {`(e.g. "BK01" or "BK01:yellow,green~BK02:blue")`}</label>
          <div className="search-wrap">
            <Search size={16}/>
            <input
              data-testid="style-search-input"
              className="input"
              placeholder="e.g. BK01 or BK01:yellow,green~BK02:blue"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
        </div>
        <button className="btn btn-primary" data-testid="style-search-btn" onClick={search} disabled={busy || !query.trim()} style={{ marginTop: 12 }}>
          {busy ? "Searching..." : "Search"}
        </button>
      </div>

      {err && <div className="error-msg" style={{ marginBottom: 16 }}>{err}</div>}

      {data && (
        <>
          <div className="card" style={{ marginBottom: 16 }} data-testid="style-summary">
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6 }}>Total Sold</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtInt(data.total_qty)} <span style={{ fontSize: 13, fontWeight: 400, color: "var(--text-muted)" }}>pieces</span></div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6 }}>Across</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{data.occurrence_count} <span style={{ fontSize: 13, fontWeight: 400, color: "var(--text-muted)" }}>invoice line(s)</span></div>
              </div>
              {Object.keys(data.color_breakdown).length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>By Color</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {Object.entries(data.color_breakdown).map(([color, qty]) => (
                      <span key={color} className="badge badge-mute">{color}: {fmtInt(qty)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card-flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice #</th><th>Date</th><th>Customer</th><th>Status</th>
                  <th>Style</th><th>Color</th><th>Sizes</th><th className="num">Qty</th>
                </tr>
              </thead>
              <tbody>
                {data.results.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text-muted)", padding: 32 }}>No sales found for this search.</td></tr>
                )}
                {data.results.map((r, i) => (
                  <tr key={i} data-testid={`style-sale-row-${i}`}>
                    <td>
                      <Link to={`/invoices/${r.invoice_id}`} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>
                        {r.invoice_number}
                      </Link>
                    </td>
                    <td>{r.invoice_date}</td>
                    <td>{r.customer_name}</td>
                    <td><StatusBadge status={r.status}/></td>
                    <td><strong>{r.style_id}</strong></td>
                    <td>{r.color}</td>
                    <td style={{ color: "var(--text-muted)", fontSize: 13 }}>
                      {Object.entries(r.sizes || {}).filter(([, q]) => Number(q) > 0).map(([s, q]) => `${s}+${q}`).join(", ")}
                    </td>
                    <td className="num"><strong>{fmtInt(r.qty)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!data && searched && !busy && !err && (
        <p style={{ color: "var(--text-muted)" }}>No results.</p>
      )}
    </div>
  );
}
